import {ofType} from 'redux-observable';
import {of, combineLatest, type Observable, merge} from 'rxjs';
import {mergeMap, map, catchError, filter, flatMap} from 'rxjs/operators';
import {actionTypes, type FormAction} from 'redux-form';

import {combineEpics} from 'web-app/util/redux-observable';

import * as API from './api';
import * as Actions from './actions';
import * as Constants from './constants';

/**
 * Epic responsible for fetching the permission descriptions that populate the table header
 */
const fetchPermissionDescriptions = action$ =>
    action$.pipe(
        ofType(Actions.fetchPermissionDescriptions.type),
        mergeMap(({payload}: ReturnType<typeof Actions.fetchPermissionDescriptions.action>) =>
            API.fetchPermissionDescriptions(payload.institutionId, payload.kind).pipe(
                map(response =>
                    Actions.fetchPermissionDescriptionsSuccess.action(payload.institutionId, payload.kind, response),
                ),
                catchError(e => of(Actions.fetchPermissionDescriptionsFailed.action(e))),
            ),
        ),
    );

/**
 * Epic responsible for picking up form changes and fetching permissions based on those
 */
const triggerFetchPermissions = action$ => {
    // Listen to the latest result of permission descriptions
    const fetchPermissionsAction: Observable<ReturnType<typeof Actions.fetchPermissionDescriptionsSuccess.action>> =
        action$.pipe(ofType(Actions.fetchPermissionDescriptionsSuccess.type));

    // Listen to form filter changes
    const filterFormChange = action$.pipe(
        ofType(actionTypes.CHANGE),
        filter((action: FormAction) => action.meta.form === Constants.FILTER_FORM_NAME),
    );
    const filterChangeObservable = combineLatest<FormAction>(
        // More form fields can be picked up here
        filterFormChange.pipe(filter((action: FormAction) => action.meta.field === 'groupId')),
    ).pipe(
        flatMap(([groupChangeAction]) =>
            of({
                groupId: groupChangeAction.payload,
            }),
        ),
    );

    // Listen to form filter initialise
    const filterFormInitialValuesObservable = action$.pipe(
        ofType(actionTypes.INITIALIZE),
        filter((action: FormAction) => action.meta.form === Constants.FILTER_FORM_NAME),
        flatMap((action: FormAction) => of(action.payload)),
    );

    // Use values from form initialise OR when the form changes - whatever is latest
    const filterValuesObservable = merge(filterChangeObservable, filterFormInitialValuesObservable);

    // Combine the permission descriptions and filter values and call the API
    return combineLatest(fetchPermissionsAction, filterValuesObservable).pipe(
        flatMap(([fetchAction, filterValues]) =>
            of(
                Actions.fetchPermissionResults.action(
                    fetchAction.payload.institutionId,
                    fetchAction.payload.kind,
                    fetchAction.payload.permissions,
                    filterValues,
                ),
            ),
        ),
    );
};

const fetchPermissions = action$ =>
    action$.pipe(
        ofType(Actions.fetchPermissionResults.type),
        flatMap(({payload}) =>
            API.fetchPermissionResults(
                payload.institutionId,
                payload.kind,
                payload.permissions.map(({id}) => id),
                payload.filterValues,
            ).pipe(
                map(response => Actions.fetchPermissionResultsSuccess.action(response.results, response.total)),
                catchError(e => of(Actions.fetchPermissionResultsSuccess.action(e))),
            ),
        ),
    );

export default combineEpics(fetchPermissionDescriptions, triggerFetchPermissions, fetchPermissions);
